/**
 * OMNI-61611: Product banner priority for the Sample Drop sign-off
 * (allocation order acknowledgement) screen.
 *
 * Only ONE banner is shown, chosen by precedence (highest first):
 *
 *   SLYND > TWIRLA > TYBLUME > NUVESSA > VITAFOL
 *
 * Everything is keyed by the BRAND PRODUCT GUID (not names), so matching is
 * exact and language/naming independent:
 *   - `priority` is an ordered list of own-banner brand product GUIDs.
 *   - `fallback.whenOnlySampled` lists the GUIDs of brands that have NO banner
 *     of their own (e.g. EVAMIST / NUVETEST); when those are the ONLY brands
 *     sampled, `fallback.bannerKey` (e.g. the SLYND brand GUID) is shown.
 *   - A brand is "sampled" when a sampled SKU maps to that brand GUID (the
 *     SKU -> brand map comes from the brandskuproducts payload).
 *
 * If any own-banner brand is also sampled, that brand wins — e.g.
 * EVAMIST + TWIRLA => TWIRLA, VITAFOL + EVAMIST => VITAFOL.
 *
 * The table is read at runtime from the BU_ALLOCATIONORDER_GA_PRODUCTBANNERPRIORITY IO
 * Configuration via AuthenticationService.getIOConfigurationValue (same as every
 * other io-config value; synced with the user config and available offline).
 * There is NO bundled fallback: missing / empty / invalid config => no banner.
 * The banner IMAGE is the winning brand's banner (downloaded for offline via the
 * brandskuproducts flow), resolved by the caller from the returned bannerKey.
 *
 * GUID comparisons are case-insensitive (Dynamics casing + the io-config
 * framework lowercasing TEXT values must not matter).
 */
export interface ProductBannerFallback {
  /** Brand product GUID whose banner is shown when the fallback fires. */
  bannerKey: string;
  /** Brand product GUIDs that have no banner of their own (trigger fallback). */
  whenOnlySampled: string[];
}

export interface ProductBannerConfig {
  /** Own-banner brand product GUIDs, precedence order (highest first). */
  priority: string[];
  /** Optional fallback for brands that have no banner of their own. */
  fallback?: ProductBannerFallback;
}

export interface ResolvedProductBanner {
  /** Winning brand product GUID — map to the brand's downloaded banner record. */
  bannerKey: string;
}

/**
 * Case-insensitive property read. AuthenticationService.getIOConfigurationValue
 * lowercases TEXT-type io-config values, so JSON keys may arrive as "priority"
 * / "bannerkey" / "whenonlysampled"; this reads them regardless of dataType.
 */
function ciProp(obj: any, key: string): any {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  if (key in obj) {
    return obj[key];
  }
  const lower = key.toLowerCase();
  const match = Object.keys(obj).find(k => k.toLowerCase() === lower);
  return match ? obj[match] : undefined;
}

/**
 * Parse the raw indskr_configvalue of the BU_ALLOCATIONORDER_GA_PRODUCTBANNERPRIORITY IO
 * Configuration. Returns null when the value is missing or does not describe a
 * usable config — the caller then shows no banner.
 *
 * Expected JSON shape (priority order === precedence, highest first);
 * every value is a BRAND PRODUCT GUID:
 * {
 *   "priority": [
 *     "<SLYND-brand-guid>",
 *     "<TWIRLA-brand-guid>",
 *     "<TYBLUME-brand-guid>",
 *     "<NUVESSA-brand-guid>",
 *     "<VITAFOL-brand-guid>"
 *   ],
 *   "fallback": {
 *     "bannerKey": "<SLYND-brand-guid>",
 *     "whenOnlySampled": ["<EVAMIST-brand-guid>", "<NUVETEST-brand-guid>"]
 *   }
 * }
 *
 * Keys are read case-insensitively because the io-config framework lowercases
 * TEXT values (see ciProp).
 */
export function parseProductBannerConfig(configValue: string | null | undefined): ProductBannerConfig | null {
  if (!configValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(configValue);
    const priorityRaw = ciProp(parsed, 'priority');
    if (!Array.isArray(priorityRaw)) {
      return null;
    }
    const priority = priorityRaw.filter((g): g is string => typeof g === 'string' && g.length > 0);
    if (priority.length === 0) {
      return null;
    }
    let fallback: ProductBannerFallback | undefined;
    const rawFallback = ciProp(parsed, 'fallback');
    const fbBannerKey = ciProp(rawFallback, 'bannerKey');
    const fbWhenOnlySampled = ciProp(rawFallback, 'whenOnlySampled');
    if (typeof fbBannerKey === 'string' && Array.isArray(fbWhenOnlySampled)) {
      fallback = {
        bannerKey: fbBannerKey,
        whenOnlySampled: fbWhenOnlySampled.filter((g): g is string => typeof g === 'string' && g.length > 0),
      };
    }
    return { priority, fallback };
  } catch {
    return null;
  }
}

/**
 * Resolve which banner to show, given the brand product GUID each sampled SKU
 * maps to (null for a SKU whose brand is unknown).
 *
 * 1. Walk `priority` (highest first); the first own-banner brand present wins.
 * 2. Otherwise, if EVERY sampled SKU maps to a known no-banner brand in
 *    `fallback.whenOnlySampled`, show the fallback banner.
 * 3. Otherwise no banner.
 *
 * Returns the winning brand GUID, or null when nothing should be shown.
 */
export function resolveProductBanner(
  sampledBrandGuids: ReadonlyArray<string | null | undefined>,
  config: ProductBannerConfig | null | undefined,
): ResolvedProductBanner | null {
  const sampled = sampledBrandGuids || [];
  const present = sampled.filter((g): g is string => !!g).map(g => g.toLowerCase());
  if (present.length === 0 || !config || !Array.isArray(config.priority) || config.priority.length === 0) {
    return null;
  }

  // 1. Highest-precedence own-banner brand that is present wins.
  for (const brandGuid of config.priority) {
    if (present.includes(brandGuid.toLowerCase())) {
      return { bannerKey: brandGuid };
    }
  }

  // 2. Fallback: only fires when EVERY sampled SKU maps to a known no-banner
  //    brand in the fallback set (an unknown/other product blocks it).
  const fallback = config.fallback;
  if (fallback && fallback.whenOnlySampled.length > 0) {
    const fallbackSet = fallback.whenOnlySampled.map(g => g.toLowerCase());
    const allAreFallbackBrands = sampled.every(g => !!g && fallbackSet.includes(g.toLowerCase()));
    if (allAreFallbackBrands) {
      return { bannerKey: fallback.bannerKey };
    }
  }

  // 3. Nothing matches.
  return null;
}
